import React from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Box,
  Stack, // MUI's Stack for simplified layout
  SvgIcon,
  useTheme, // To access theme spacing and palettes
  useMediaQuery // For responsive adjustments if needed
} from '@mui/material';
import {
  EventAvailable as EventAvailableIcon
} from '@mui/icons-material';

// Custom Clapping Hands Icon (same as before, assuming this is the desired icon)
const ClappingHandsIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24"> {/* Adjusted viewBox for clarity */}
    <path d="M19 12H5.83l3.58-3.59L8 7l-6 6 6 6 1.41-1.41L5.83 14H19v5h2v-7c0-1.1-.9-2-2-2zm-7-2V3c0-.55-.45-1-1-1s-1 .45-1 1v7h2zm4 2c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5S16.83 12 16 12zm-8 0c-.83 0-1.5.67-1.5 1.5S7.17 15 8 15s1.5-.67 1.5-1.5S8.83 12 8 12z" />
    {/* Note: The previous path seemed more like a generic gesture.
        A more direct representation of clapping hands might be:
        <path d="M14.59 13.41l-1.18 1.18-1.41-1.41 1.18-1.18-3.18-3.18-.71.71L12 12l-1.18-1.18-1.41 1.41L10.59 13.41l-3.18 3.18.71.71L12 14l1.18 1.18 1.41-1.41L13.41 12.59l3.18-3.18-.71-.71L12 12z"/>
        For the purpose of this rewrite, I'm keeping a placeholder, but ensure you have the correct SVG path for "clapping hands".
        The original image's icon is quite stylized (like an emoji 👏).
        A simple text emoji could also be an option: <Typography sx={{ fontSize: '2.5rem' }}>👏</Typography>
    */}
  </SvgIcon>
);


const PaymentConfirmationModal = ({
  open,
  onClose,
  userName = "Jessica", // Renamed for clarity from 'name'
  doctorName = "Dr. Williams", // Renamed for clarity
  onAddToCalendar,
  onDoAnotherTime
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleAddToCalendar = () => {
    if (onAddToCalendar) onAddToCalendar();
    onClose(); // Typically close modal after action
  };

  const handleDoAnotherTime = () => {
    if (onDoAnotherTime) onDoAnotherTime();
    onClose(); // Close modal
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          width: isMobile ? '90%' : 'auto', // Responsive width
          maxWidth: '400px', // Max width for consistency
          margin: 2,
        }
      }}
      aria-labelledby="confirmation-dialog-title"
    >
      <DialogContent
        sx={{
          padding: theme.spacing(isMobile ? 3 : 4), // Responsive padding
        }}
      >
        <Stack spacing={2} alignItems="center">
          <Box
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.1)', // Lighter green, closer to image
              borderRadius: '50%',
              width: theme.spacing(9), // 72px
              height: theme.spacing(9),
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {/* Using a text emoji for simplicity here, as SVGs can be complex.
                Replace with your preferred SVGIcon if needed. */}
            <Typography component="span" sx={{ fontSize: '2.8rem' }} role="img" aria-label="Clapping Hands">
              👏
            </Typography>
          </Box>

          <Typography
            id="confirmation-dialog-title"
            variant="h6"
            component="h2" // More semantic for a dialog title
            textAlign="center"
            sx={{ fontWeight: 'medium' }}
          >
            Hello {userName}, good news!
          </Typography>

          <Typography variant="body1" textAlign="center" color="text.secondary">
            Your appointment with {doctorName} has been confirmed.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            startIcon={<EventAvailableIcon />}
            onClick={handleAddToCalendar}
            sx={{
              backgroundColor: '#4caf50', // Green button from image
              color: 'white',
              '&:hover': {
                backgroundColor: '#388e3c',
              },
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none', // As per image, button text is not all caps
              fontWeight: 'bold',
              fontSize: '1rem',
              mt: 2 // Margin top
            }}
          >
            Add to Calendar
          </Button>

          <Button
            variant="text"
            fullWidth
            onClick={handleDoAnotherTime}
            sx={{
              color: 'text.secondary',
              textTransform: 'none', // As per image
              fontWeight: 'medium',
              '&:hover': {
                backgroundColor: 'action.hover'
              },
              py: 1,
            }}
          >
            I'll do another time
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

// Example of how to use it (keep this commented out or move to your App.js):
/*
import { useState } from 'react';
// import PaymentConfirmationModal from './PaymentConfirmationModal'; // Adjust path as needed
// import Button from '@mui/material/Button'; // Already imported in Modal if using there

function App() {
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleOpenConfirmation = () => {
    setOpenConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const handleCalendarAction = () => {
    console.log('Add to calendar action triggered');
    // Implement actual add to calendar logic here
  };

  const handleAnotherTimeAction = () => {
    console.log("'I'll do another time' action triggered");
  };


  return (
    <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
      <Button onClick={handleOpenConfirmation} variant="contained">
        Show Confirmation Modal
      </Button>
      <PaymentConfirmationModal
        open={openConfirmation}
        onClose={handleCloseConfirmation}
        userName="Jessica"
        doctorName="Dr. Williams"
        onAddToCalendar={handleCalendarAction}
        onDoAnotherTime={handleAnotherTimeAction}
      />
    </Box>
  );
}

export default App;
*/

export default PaymentConfirmationModal;